import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Users as UsersIcon,
  Search,
  Filter,
  ChevronRight,
  Download,
  Mail,
  Tag,
  ArrowLeft,
  ArrowUpDown,
  Calendar,
  UserCheck,
  DollarSign,
  Loader2,
  Menu,
  X
} from 'lucide-react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../../components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../components/ui/sheet';
import { cn } from '../../lib/utils';
import UserDetailPanel from './UserDetail';
import UsersTable from '../../components/users/UsersTable';
import { useTheme } from '../../hooks/useTheme';
import { getEnhancedUserData } from '../../services/analyticsService';

const ITEMS_PER_PAGE = 50;

const UserInsights = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTimeRange, setSelectedTimeRange] = useState(30);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'valueScore', direction: 'desc' });
  const [activeFilters, setActiveFilters] = useState({
    journeyStage: 'all',
    timeRange: 'all',
    activity: 'all'
  });
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalUsers, setTotalUsers] = useState(0);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const theme = useTheme();

  // Fetch real user data from Firebase with proper analytics-based journey stages
  const fetchUsers = async () => {
    setLoading(true);
    try {
      console.log('🔍 Fetching users from Firebase...');

      // Get enhanced user data with analytics events
      const enhancedUsers = await getEnhancedUserData(1000);
      console.log('📊 Enhanced users fetched:', enhancedUsers.length);

      if (enhancedUsers && enhancedUsers.length > 0) {
        // Process users with proper journey stage logic based on analytics events
        const processedUsers = enhancedUsers.map(user => {
          // Calculate journey stage based on actual analytics events from readme
          let journeyStage = 'new';

          // Check for analytics events in order of funnel progression
          const hasSessionStart = user.totalEvents > 0; // Any analytics event indicates engagement
          const hasResumeStart = user.saveEventsCount > 0 || user.downloadEventsCount > 0;
          const hasResumeSave = user.saveEventsCount > 0;
          const hasResumeDownload = user.downloadEventsCount > 0 || user.hasPaid;

          if (hasResumeDownload || user.hasPaid) {
            journeyStage = 'paid';
          } else if (hasResumeSave) {
            journeyStage = 'saved';
          } else if (hasResumeStart) {
            journeyStage = 'started';
          } else if (hasSessionStart) {
            journeyStage = 'engaged';
          }

          // Calculate value score based on user activity and engagement
          let valueScore = 30; // Base score for new users
          if (journeyStage === 'paid') valueScore = 90;
          else if (journeyStage === 'saved') valueScore = 70;
          else if (journeyStage === 'started') valueScore = 50;
          else if (journeyStage === 'engaged') valueScore = 40;

          // Add AI usage bonus
          if (user.aiEventsCount > 0) {
            valueScore += Math.min(20, user.aiEventsCount * 2);
          }

          // Add subscription bonus
          if (user.isSubscribed) {
            valueScore += 10;
          }

          valueScore = Math.min(100, valueScore);

          // Generate tags based on user behavior
          const tags = [];
          if (user.isSubscribed) tags.push('VIP');
          if (user.hasPaid) tags.push('Paid');
          if (user.aiEventsCount > 0) tags.push('AI User');
          if (user.downloadEventsCount > 0) tags.push('Downloader');
          if (user.saveEventsCount > 0) tags.push('Saver');
          if (journeyStage === 'new') tags.push('New User');

          return {
            ...user,
            journeyStage,
            valueScore,
            tags,
            // Ensure last activity is calculated from analytics events
            lastActivity: user.lastActivity || user.createdAt,
            lastActivityTimestamp: user.lastActivity || user.createdAt
          };
        });

        console.log('🔄 Processed users with analytics-based journey stages:', processedUsers.length);
        setAllUsers(processedUsers);
        setTotalUsers(processedUsers.length);
      } else {
        setAllUsers([]);
        setTotalUsers(0);
      }
    } catch (error) {
      console.error('❌ Error fetching users:', error);
      setAllUsers([]);
      setTotalUsers(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch users on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle back navigation
  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from);
    } else {
      navigate('/users');
    }
  };

  // Filter and sort users with improved analytics-based logic
  const filterAndSortUsers = () => {
    setLoading(true);
    try {
      console.log('🔍 Filtering and sorting users...');
      let filteredUsers = [...allUsers];

      // Apply search filter
      if (searchQuery) {
        const searchTerm = searchQuery.toLowerCase();
        filteredUsers = filteredUsers.filter(user =>
          (user.displayName || '').toLowerCase().includes(searchTerm) ||
          (user.email || '').toLowerCase().includes(searchTerm) ||
          (user.tags || []).some(tag => tag.toLowerCase().includes(searchTerm))
        );
      }

      // Apply journey stage filter
      if (activeFilters.journeyStage !== 'all') {
        filteredUsers = filteredUsers.filter(user =>
          user.journeyStage === activeFilters.journeyStage
        );
      }

      // Apply time range filter based on last activity from analytics
      if (activeFilters.timeRange !== 'all') {
        const timeRangeInDays = parseInt(activeFilters.timeRange);
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - timeRangeInDays);

        filteredUsers = filteredUsers.filter(user => {
          const lastActivity = user.lastActivity || user.createdAt;
          const lastActivityDate = lastActivity?.toDate?.() || new Date(lastActivity);
          return lastActivityDate >= cutoffDate;
        });
      }

      // Apply activity filter based on analytics events
      if (activeFilters.activity !== 'all') {
        const now = new Date();
        const activityThreshold = new Date(now.setHours(now.getHours() - 24));

        filteredUsers = filteredUsers.filter(user => {
          const lastActivity = user.lastActivity || user.createdAt;
          const lastActivityDate = lastActivity?.toDate?.() || new Date(lastActivity);
          return activeFilters.activity === 'active'
            ? lastActivityDate >= activityThreshold
            : lastActivityDate < activityThreshold;
        });
      }

      // Apply sorting with improved logic
      filteredUsers.sort((a, b) => {
        let comparison = 0;
        switch (sortConfig.key) {
          case 'name':
            comparison = (a.displayName || '').localeCompare(b.displayName || '');
            break;
          case 'valueScore':
            comparison = (b.valueScore || 0) - (a.valueScore || 0);
            break;
          case 'lastActivity':
            const aLastActivity = a.lastActivity || a.createdAt;
            const bLastActivity = b.lastActivity || b.createdAt;
            const aDate = aLastActivity?.toDate?.() || new Date(aLastActivity);
            const bDate = bLastActivity?.toDate?.() || new Date(bLastActivity);
            comparison = bDate - aDate;
            break;
          case 'journeyStage':
            const stages = ['new', 'engaged', 'started', 'saved', 'paid'];
            comparison = stages.indexOf(b.journeyStage || 'new') - stages.indexOf(a.journeyStage || 'new');
            break;
          default:
            comparison = 0;
        }
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });

      // Apply pagination
      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
      const paginatedUsers = filteredUsers.slice(startIndex, startIndex + ITEMS_PER_PAGE);

      setUsers(paginatedUsers);
      setTotalUsers(filteredUsers.length);
    } catch (error) {
      console.error('Error filtering users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Effect to handle filtering and sorting
  useEffect(() => {
    if (allUsers.length > 0) {
      filterAndSortUsers();
    }
  }, [searchQuery, activeFilters, sortConfig, currentPage, allUsers]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers.filter(id => id !== userId));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUsers(users.map(user => user.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleUserClick = (user) => {
    setSelectedUser(user);
    setShowUserDetail(true);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setSelectedUsers([]);
  };

  const totalPages = Math.ceil(totalUsers / ITEMS_PER_PAGE);

  const renderPagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      pages.push(
        <Button
          key="1"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 hover:bg-muted"
          onClick={() => handlePageChange(1)}
        >
          1
        </Button>
      );
      if (startPage > 2) {
        pages.push(<span key="ellipsis1" className="px-2 text-muted-foreground">...</span>);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={currentPage === i ? "secondary" : "ghost"}
          size="sm"
          className={cn(
            "h-8 w-8 p-0",
            currentPage === i ? "bg-muted" : "hover:bg-muted/50"
          )}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Button>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<span key="ellipsis2" className="px-2 text-muted-foreground">...</span>);
      }
      pages.push(
        <Button
          key={totalPages}
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 hover:bg-muted"
          onClick={() => handlePageChange(totalPages)}
        >
          {totalPages}
        </Button>
      );
    }

    return pages;
  };

  // Mobile filter component
  const MobileFilters = () => (
    <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
      <SheetContent side="right" className="w-[300px] sm:w-[400px]">
        <SheetHeader>
          <SheetTitle>Filters</SheetTitle>
        </SheetHeader>
        <div className="space-y-6 mt-6">
          {/* Time Range Filter */}
          <div className="space-y-3">
            <h3 className="font-medium">Time Range</h3>
            <div className="space-y-2">
              {[
                { value: 'all', label: 'All Time' },
                { value: '7', label: 'Last 7 days' },
                { value: '30', label: 'Last 30 days' },
                { value: '90', label: 'Last 90 days' }
              ].map((option) => (
                <Button
                  key={option.value}
                  variant={activeFilters.timeRange === option.value ? "secondary" : "ghost"}
                  className="w-full justify-start"
                  onClick={() => {
                    setActiveFilters(prev => ({ ...prev, timeRange: option.value }));
                    setShowMobileFilters(false);
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Activity Filter */}
          <div className="space-y-3">
            <h3 className="font-medium">Activity</h3>
            <div className="space-y-2">
              {[
                { value: 'all', label: 'All Users' },
                { value: 'active', label: 'Active Users' },
                { value: 'inactive', label: 'Inactive Users' }
              ].map((option) => (
                <Button
                  key={option.value}
                  variant={activeFilters.activity === option.value ? "secondary" : "ghost"}
                  className="w-full justify-start"
                  onClick={() => {
                    setActiveFilters(prev => ({ ...prev, activity: option.value }));
                    setShowMobileFilters(false);
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Journey Stage Filter */}
          <div className="space-y-3">
            <h3 className="font-medium">Journey Stage</h3>
            <div className="space-y-2">
              {[
                { value: 'all', label: 'All Stages' },
                { value: 'paid', label: 'Paid Users' },
                { value: 'saved', label: 'Saved Resume' },
                { value: 'started', label: 'Started Resume' },
                { value: 'engaged', label: 'Engaged (Session)' },
                { value: 'new', label: 'New Users' }
              ].map((option) => (
                <Button
                  key={option.value}
                  variant={activeFilters.journeyStage === option.value ? "secondary" : "ghost"}
                  className="w-full justify-start"
                  onClick={() => {
                    setActiveFilters(prev => ({ ...prev, journeyStage: option.value }));
                    setShowMobileFilters(false);
                  }}
                >
                  {option.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <div className="h-full flex flex-col overflow-hidden bg-background">
      {/* Mobile Filters */}
      <MobileFilters />

      {/* Header - Mobile Responsive */}
      <div className="border-b border-border">
        <div className="px-4 sm:px-6 py-4">
          {/* Mobile Header */}
          <div className="flex items-center justify-between sm:hidden">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Button
                variant="ghost"
                size="sm"
                className="p-2 flex-shrink-0"
                onClick={handleBack}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="min-w-0 flex-1">
                <h1 className="text-lg font-semibold text-foreground truncate">User Insights</h1>
                <p className="text-xs text-muted-foreground truncate">
                  {totalUsers} users • {selectedUsers.length} selected
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="p-2"
                onClick={() => setShowMobileFilters(true)}
              >
                <Filter className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Desktop Header */}
          <div className="hidden sm:flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                className="gap-2 bg-muted hover:bg-muted/80 text-foreground"
                onClick={handleBack}
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Overview
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-foreground">User Insights</h1>
                <p className="text-sm text-muted-foreground">
                  {totalUsers} total users • {selectedUsers.length} selected
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8 w-[300px] bg-muted border-border text-foreground hover:border-border/80 focus:border-primary"
                />
              </div>

              {/* Desktop Filters */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 bg-muted hover:bg-muted/80 border-border text-foreground"
                  >
                    <Calendar className="h-4 w-4" />
                    Time Range
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[200px] bg-card border-border">
                  <DropdownMenuItem onClick={() => setActiveFilters(prev => ({ ...prev, timeRange: 'all' }))}>
                    All Time
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveFilters(prev => ({ ...prev, timeRange: '7' }))}>
                    Last 7 days
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveFilters(prev => ({ ...prev, timeRange: '30' }))}>
                    Last 30 days
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveFilters(prev => ({ ...prev, timeRange: '90' }))}>
                    Last 90 days
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2 bg-muted hover:bg-muted/80 border-border text-foreground">
                    <UserCheck className="h-4 w-4" />
                    Activity
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[200px] bg-card border-border">
                  <DropdownMenuItem onClick={() => setActiveFilters(prev => ({ ...prev, activity: 'all' }))}>
                    All Users
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveFilters(prev => ({ ...prev, activity: 'active' }))}>
                    Active Users
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveFilters(prev => ({ ...prev, activity: 'inactive' }))}>
                    Inactive Users
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2 bg-muted hover:bg-muted/80 border-border text-foreground">
                    <DollarSign className="h-4 w-4" />
                    Journey Stage
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[200px] bg-card border-border">
                  <DropdownMenuItem onClick={() => setActiveFilters(prev => ({ ...prev, journeyStage: 'all' }))}>
                    All Stages
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setActiveFilters(prev => ({ ...prev, journeyStage: 'paid' }))}>
                    Paid Users
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveFilters(prev => ({ ...prev, journeyStage: 'saved' }))}>
                    Saved Resume
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveFilters(prev => ({ ...prev, journeyStage: 'started' }))}>
                    Started Resume
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveFilters(prev => ({ ...prev, journeyStage: 'engaged' }))}>
                    Engaged (Session)
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setActiveFilters(prev => ({ ...prev, journeyStage: 'new' }))}>
                    New Users
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {selectedUsers.length > 0 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 bg-muted hover:bg-muted/80 border-border text-foreground"
                    onClick={() => console.log('Send email to:', selectedUsers)}
                  >
                    <Mail className="h-4 w-4" />
                    Email Selected
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 bg-muted hover:bg-muted/80 border-border text-foreground"
                    onClick={() => console.log('Add tags to:', selectedUsers)}
                  >
                    <Tag className="h-4 w-4" />
                    Add Tags
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-2 bg-muted hover:bg-muted/80 border-border text-foreground"
                    onClick={() => console.log('Export:', selectedUsers)}
                  >
                    <Download className="h-4 w-4" />
                    Export Data
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Search */}
          <div className="mt-4 sm:hidden">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-muted border-border text-foreground h-10"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Content - Mobile Responsive */}
      <div className="flex-1 p-4 sm:p-6 space-y-6 overflow-y-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center h-64 space-y-4">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <p className="text-muted-foreground">Loading user data...</p>
          </div>
        ) : users.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 space-y-4">
            <UsersIcon className="h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">No users found</p>
            <p className="text-sm text-muted-foreground text-center">Try adjusting your filters or search terms</p>
          </div>
        ) : (
          <>
            <div className="rounded-lg border border-border overflow-hidden bg-card">
              <UsersTable
                users={users}
                selectedUsers={selectedUsers}
                onSelectUser={handleSelectUser}
                onSelectAll={handleSelectAll}
                onUserClick={handleUserClick}
                sortConfig={sortConfig}
                onSort={handleSort}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                totalUsers={totalUsers}
              />
            </div>

            {/* Pagination - Mobile Responsive */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 px-2">
              <div className="text-sm text-muted-foreground text-center sm:text-left">
                Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, totalUsers)} of {totalUsers} users
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-foreground hover:bg-muted"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                >
                  Previous
                </Button>
                {renderPagination()}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-foreground hover:bg-muted"
                  disabled={currentPage === totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* User Detail Side Panel */}
      <UserDetailPanel
        user={selectedUser}
        isOpen={showUserDetail}
        onClose={() => {
          setShowUserDetail(false);
          setSelectedUser(null);
        }}
      />
    </div>
  );
};

export default UserInsights; 